import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import { putRemito } from "../helpers/rutaRemitos";
import { getProveedores } from "../helpers/rutaProveedores";
import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";
import { getChoferes } from "../helpers/rutaChoferes";

const VARIEDADES_SUGERIDAS = ["ATLANTIC", "INNOVATOR", "SPUNTA", "BATATA"];

const parseNumber = (value, def = 0) => {
  if (value === "" || value === null || value === undefined) return def;

  const n = Number(value);
  return Number.isNaN(n) ? def : n;
};

const money = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dateToInput = (value) => {
  if (!value) return "";

  const s = String(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "";

  return d.toISOString().slice(0, 10);
};

const getId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || value.id || "";
  return value;
};

const normalizarArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const sortByText = (arr, getter) => {
  return [...(arr || [])].sort((a, b) =>
    String(getter(a) || "").localeCompare(String(getter(b) || ""), "es", {
      sensitivity: "base",
    })
  );
};

const getNombreProveedor = (p) =>
  p?.razonsocial || p?.nombre || p?.proveedor || p?.descripcion || "";

const getNombreCliente = (c) =>
  c?.razonsocial || c?.nombre || c?.cliente || c?.descripcion || "";

const getNombreTransporte = (t) =>
  t?.transporte || t?.razonsocial || t?.nombre || t?.descripcion || "";

const getNombreChofer = (c) =>
  c?.nombre || c?.apellido || c?.razonsocial || c?.descripcion || "";

const makeInitialForm = () => ({
  fecha: "",
  nroviaje: "",

  kgCampo: "",
  kgRecibido: "",

  precioCompra: "",
  precioVenta: "",

  proveedor: "",
  cliente: "",
  transporte: "",
  chofer: "",

  variedad: "",
  nroDTVCompra: "",
  nroDTVVenta: "",

  porcentajeCompra: 0,
  porcentajeVenta: 0,

  flete: 0,
  facturacompra: "",
  facturaventa: "",

  nota: "",
  observaciones: "",
  factura: true,
});

const ModalFormRemito = ({
  show,
  remito,
  onClose,
  handleClose,
  onSaved,
}) => {
  const cerrar = onClose || handleClose || (() => {});

  const [form, setForm] = useState(makeInitialForm());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [listas, setListas] = useState({
    proveedores: [],
    clientes: [],
    transportes: [],
    choferes: [],
    loading: true,
    error: "",
  });

  useEffect(() => {
    let mounted = true;

    const cargarListas = async () => {
      try {
        setListas((prev) => ({
          ...prev,
          loading: true,
          error: "",
        }));

        const [provResp, cliResp, traResp, chofResp] = await Promise.all([
          getProveedores(),
          getClientes(),
          getTransportes(),
          getChoferes(),
        ]);

        if (!mounted) return;

        setListas({
          proveedores: normalizarArray(provResp, "proveedores"),
          clientes: normalizarArray(cliResp, "clientes"),
          transportes: normalizarArray(traResp, "transportes"),
          choferes: normalizarArray(chofResp, "choferes"),
          loading: false,
          error: "",
        });
      } catch (e) {
        if (!mounted) return;

        setListas((prev) => ({
          ...prev,
          loading: false,
          error: e?.message || "Error al cargar listas",
        }));
      }
    };

    cargarListas();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!show || !remito) return;

    setForm({
      fecha: dateToInput(remito.fecha),
      nroviaje: remito.nroviaje || "",

      kgCampo: remito.kgCampo ?? "",
      kgRecibido: remito.kgRecibido ?? "",

      precioCompra: remito.precioCompra ?? "",
      precioVenta: remito.precioVenta ?? "",

      proveedor: getId(remito.proveedor),
      cliente: getId(remito.cliente),
      transporte: getId(remito.transporte),
      chofer: getId(remito.chofer),

      variedad: remito.variedad || "",
      nroDTVCompra: remito.nroDTVCompra || "",
      nroDTVVenta: remito.nroDTVVenta || "",

      porcentajeCompra: remito.porcentajeCompra ?? 0,
      porcentajeVenta: remito.porcentajeVenta ?? 0,

      flete: remito.flete ?? 0,
      facturacompra: remito.facturacompra || "",
      facturaventa: remito.facturaventa || "",

      nota: remito.nota || "",
      observaciones: remito.observaciones || "",
      factura: remito.factura ?? true,
    });

    setError("");
    setSaved(false);
    setSaving(false);
  }, [show, remito]);

  const proveedoresOrdenados = useMemo(
    () => sortByText(listas.proveedores, getNombreProveedor),
    [listas.proveedores]
  );

  const clientesOrdenados = useMemo(
    () => sortByText(listas.clientes, getNombreCliente),
    [listas.clientes]
  );

  const transportesOrdenados = useMemo(
    () => sortByText(listas.transportes, getNombreTransporte),
    [listas.transportes]
  );

  const choferesOrdenados = useMemo(
    () => sortByText(listas.choferes, getNombreChofer),
    [listas.choferes]
  );

  const handle = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const kgCampoNum = parseNumber(form.kgCampo);
  const kgRecibidoNum = parseNumber(form.kgRecibido);
  const precioCompraNum = parseNumber(form.precioCompra);
  const precioVentaNum = parseNumber(form.precioVenta);

  const diferencia = kgCampoNum - kgRecibidoNum;

  const baseCompra = kgRecibidoNum * precioCompraNum;
  const baseVenta = kgRecibidoNum * precioVentaNum;

  const pctCompraNegro = Math.min(
    Math.max(parseNumber(form.porcentajeCompra) / 100, 0),
    1
  );

  const pctVentaNegro = Math.min(
    Math.max(parseNumber(form.porcentajeVenta) / 100, 0),
    1
  );

  const IVA_105 = 0.105;

  const compraB = baseCompra * pctCompraNegro;
  const compraA = baseCompra * (1 - pctCompraNegro) * (1 + IVA_105);

  const ventaB = baseVenta * pctVentaNegro;
  const ventaA = baseVenta * (1 - pctVentaNegro) * (1 + IVA_105);

  const totalCompra = compraA + compraB;
  const totalVenta = ventaA + ventaB;
  const comision = totalVenta - totalCompra;

  const validar = () => {
    if (!remito?._id) return "No se encontró el ID del remito.";
    if (!form.fecha) return "La fecha es obligatoria.";
    if (!form.proveedor) return "Debe seleccionar un proveedor.";
    if (!form.cliente) return "Debe seleccionar un cliente.";
    if (!form.transporte) return "Debe seleccionar un transporte.";
    if (!form.chofer) return "Debe seleccionar un chofer.";

    if (parseNumber(form.kgCampo) <= 0) {
      return "Kg campo debe ser mayor a cero.";
    }

    if (parseNumber(form.kgRecibido) <= 0) {
      return "Kg recibido debe ser mayor a cero.";
    }

    if (parseNumber(form.precioCompra) < 0) {
      return "Precio compra no puede ser negativo.";
    }

    if (parseNumber(form.precioVenta) < 0) {
      return "Precio venta no puede ser negativo.";
    }

    if (
      parseNumber(form.porcentajeCompra) < 0 ||
      parseNumber(form.porcentajeCompra) > 100
    ) {
      return "% compra negro debe estar entre 0 y 100.";
    }

    if (
      parseNumber(form.porcentajeVenta) < 0 ||
      parseNumber(form.porcentajeVenta) > 100
    ) {
      return "% venta negro debe estar entre 0 y 100.";
    }

    return "";
  };

  const submit = async (e) => {
    e.preventDefault();

    const msg = validar();

    if (msg) {
      setError(msg);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        fecha: form.fecha,
        nroviaje: String(form.nroviaje || "").trim(),

        kgCampo: parseNumber(form.kgCampo),
        kgRecibido: parseNumber(form.kgRecibido),

        precioCompra: parseNumber(form.precioCompra),
        precioVenta: parseNumber(form.precioVenta),

        proveedor: form.proveedor,
        cliente: form.cliente,
        transporte: form.transporte,
        chofer: form.chofer,

        // No usamos lote por ahora
        lote: null,

        variedad: (form.variedad || "").trim().toUpperCase(),

        nroDTVCompra: (form.nroDTVCompra || "").trim(),
        nroDTVVenta: (form.nroDTVVenta || "").trim(),

        porcentajeCompra: parseNumber(form.porcentajeCompra),
        porcentajeVenta: parseNumber(form.porcentajeVenta),

        flete: parseNumber(form.flete),

        facturacompra: (form.facturacompra || "").trim(),
        facturaventa: (form.facturaventa || "").trim(),

        nota: form.nota || "",
        observaciones: form.observaciones || "",
        factura: !!form.factura,
      };

      const resp = await putRemito(remito._id, payload);

      if (resp?.ok === false) {
        const backendMsg =
          resp?.err?.message ||
          resp?.error?.err?.message ||
          resp?.error?.message ||
          resp?.error ||
          "No se pudo modificar el remito.";

        throw new Error(
          typeof backendMsg === "string"
            ? backendMsg
            : "No se pudo modificar el remito."
        );
      }

      setSaved(true);

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (e2) {
      setError(e2?.message || "Error al modificar el remito.");
    } finally {
      setSaving(false);
    }
  };

  const cerrarSaved = () => {
    setSaved(false);
    cerrar();
  };

  return (
    <>
      <Modal show={show} onHide={cerrar} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            Modificar Remito {remito?.codrem ? `N° ${remito.codrem}` : ""}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={submit}>
          <Modal.Body>
            {listas.loading && (
              <div className="alert alert-info mb-3">
                Cargando datos...
              </div>
            )}

            {listas.error && (
              <div className="alert alert-warning mb-3">
                {listas.error}
              </div>
            )}

            {error && (
              <div className="alert alert-danger mb-3">
                {error}
              </div>
            )}

            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label">Fecha</label>
                <input
                  type="date"
                  className="form-control"
                  name="fecha"
                  value={form.fecha}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Nro Viaje</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroviaje"
                  value={form.nroviaje}
                  onChange={handle}
                  placeholder="Ej: 1301,1"
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Proveedor</label>
                <select
                  className="form-select"
                  name="proveedor"
                  value={form.proveedor}
                  onChange={handle}
                  required
                >
                  <option value="">Seleccionar proveedor</option>
                  {proveedoresOrdenados.map((p) => (
                    <option key={p._id} value={p._id}>
                      {getNombreProveedor(p)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Cliente</label>
                <select
                  className="form-select"
                  name="cliente"
                  value={form.cliente}
                  onChange={handle}
                  required
                >
                  <option value="">Seleccionar cliente</option>
                  {clientesOrdenados.map((c) => (
                    <option key={c._id} value={c._id}>
                      {getNombreCliente(c)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">Variedad</label>
                <input
                  list="variedadesRemitoEdit"
                  className="form-control"
                  name="variedad"
                  value={form.variedad}
                  onChange={handle}
                  placeholder="Ej: SPUNTA"
                />

                <datalist id="variedadesRemitoEdit">
                  {VARIEDADES_SUGERIDAS.map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              </div>

              <div className="col-md-2">
                <label className="form-label">Kg Campo</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="kgCampo"
                  value={form.kgCampo}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Kg Recibido</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="kgRecibido"
                  value={form.kgRecibido}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Diferencia</label>
                <input
                  type="text"
                  className="form-control"
                  value={money(diferencia)}
                  readOnly
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Flete</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="flete"
                  value={form.flete}
                  onChange={handle}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Precio Compra</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="precioCompra"
                  value={form.precioCompra}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Precio Venta</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="precioVenta"
                  value={form.precioVenta}
                  onChange={handle}
                  required
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">% Compra Negro</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  name="porcentajeCompra"
                  value={form.porcentajeCompra}
                  onChange={handle}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">% Venta Negro</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                  name="porcentajeVenta"
                  value={form.porcentajeVenta}
                  onChange={handle}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Transporte</label>
                <select
                  className="form-select"
                  name="transporte"
                  value={form.transporte}
                  onChange={handle}
                  required
                >
                  <option value="">Seleccionar transporte</option>
                  {transportesOrdenados.map((t) => (
                    <option key={t._id} value={t._id}>
                      {getNombreTransporte(t)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Chofer</label>
                <select
                  className="form-select"
                  name="chofer"
                  value={form.chofer}
                  onChange={handle}
                  required
                >
                  <option value="">Seleccionar chofer</option>
                  {choferesOrdenados.map((c) => (
                    <option key={c._id} value={c._id}>
                      {getNombreChofer(c)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2">
                <label className="form-label">DTV Compra</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroDTVCompra"
                  value={form.nroDTVCompra}
                  onChange={handle}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">DTV Venta</label>
                <input
                  type="text"
                  className="form-control"
                  name="nroDTVVenta"
                  value={form.nroDTVVenta}
                  onChange={handle}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Factura Compra</label>
                <input
                  type="text"
                  className="form-control"
                  name="facturacompra"
                  value={form.facturacompra}
                  onChange={handle}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">Factura Venta</label>
                <input
                  type="text"
                  className="form-control"
                  name="facturaventa"
                  value={form.facturaventa}
                  onChange={handle}
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Nota</label>
                <textarea
                  className="form-control"
                  name="nota"
                  value={form.nota}
                  onChange={handle}
                  rows="2"
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Observaciones</label>
                <textarea
                  className="form-control"
                  name="observaciones"
                  value={form.observaciones}
                  onChange={handle}
                  rows="2"
                />
              </div>

              <div className="col-md-12">
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="factura"
                    id="facturaRemitoEdit"
                    checked={!!form.factura}
                    onChange={handle}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="facturaRemitoEdit"
                  >
                    Incluir flete en cuenta proveedor
                  </label>
                </div>
              </div>
            </div>

            <hr />

            <div className="row g-3">
              <div className="col-md-3">
                <div className="card border-primary">
                  <div className="card-body">
                    <h6 className="text-primary mb-2">Compra A</h6>
                    <strong>$ {money(compraA)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card border-primary">
                  <div className="card-body">
                    <h6 className="text-primary mb-2">Compra B</h6>
                    <strong>$ {money(compraB)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card border-danger">
                  <div className="card-body">
                    <h6 className="text-danger mb-2">Venta A</h6>
                    <strong>$ {money(ventaA)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="card border-danger">
                  <div className="card-body">
                    <h6 className="text-danger mb-2">Venta B</h6>
                    <strong>$ {money(ventaB)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="mb-2">Total Compra</h6>
                    <strong>$ {money(totalCompra)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="mb-2">Total Venta</h6>
                    <strong>$ {money(totalVenta)}</strong>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="mb-2">Comisión</h6>
                    <strong>$ {money(comision)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={cerrar} disabled={saving}>
              Cancelar
            </Button>

            <Button type="submit" variant="dark" disabled={saving}>
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={saved} onHide={cerrarSaved} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remito modificado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          El remito se modificó correctamente.
        </Modal.Body>

        <Modal.Footer>
          <Button variant="dark" onClick={cerrarSaved}>
            Aceptar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModalFormRemito;